import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { moduleConfig } from './config';
import { buildListQuery, ListQueryOptions, normalizePaginatedListResponse, PaginatedApiResponse } from '../../../services/paginatedList';

export interface AccDocumentUser {
  id: number;
  userDisplayName?: string | null;
  userFirstName: string;
  userLastName: string;
  userEmail: string;
}

export interface AccDocumentLineTotals {
  debitLocal: number;
  creditLocal: number;
  debitUSD: number;
  creditUSD: number;
  isBalanced?: boolean;
}

export interface AccDocumentLine {
  id: number;
  companyId: number;
  accountId: number;
  debitLocal: number | string;
  creditLocal: number | string;
  rate?: number | string | null;
  debitUSD?: number | string;
  creditUSD?: number | string;
  currencyId?: number | null;
  narrative?: string | null;
  plPeriodFrom?: string | null;
  plPeriodTo?: string | null;
  dn?: string | null;
  notes?: string | null;
  linkNumber?: string | null;
  dueDate?: string | null;
  lineOrder: number;
  company?: { id: number; name: string; code: string };
  account?: { id: number; code: string; name: string; allowOpenItems?: boolean };
  currency?: { id: number; code: string; symbol?: string | null };
}

export interface AccDocumentAttachment {
  id: number;
  fileName: string;
  mimeType?: string;
  fileSize?: number;
  uploadedAt?: string;
}

export interface AccDocument {
  id: number;
  code?: string | null;
  documentTypeId: number;
  documentType?: { id: number; code: string; name: string };
  documentDate?: string | null;
  postingDate?: string | null;
  description?: string | null;
  status: 'DRAFT' | 'TEMP' | 'SAVED';
  lines?: AccDocumentLine[];
  attachments?: AccDocumentAttachment[];
  lineTotals?: AccDocumentLineTotals;
  companyCodes?: string[];
  periodLocked?: boolean;
  _count?: { lines: number; attachments?: number };
  createdByUser?: AccDocumentUser | null;
  updatedByUser?: AccDocumentUser | null;
  createdAt: string;
  updatedAt: string;
}

export const useAccDocuments = (searchParams?: string, page = 1, limit = 25, options?: ListQueryOptions) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<PaginatedApiResponse<AccDocument> | AccDocument[]>(
    `${moduleConfig.module.api.list}${q}`,
    fetcher,
  );
  const normalized = normalizePaginatedListResponse<AccDocument>(data, page, limit);
  return { data: normalized.data, total: normalized.total, isLoading, isError: error, mutate };
};

export const useAccDocument = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<AccDocument>(id ? moduleConfig.module.api.detail(id) : null, fetcher);
  return { data, item: data, isLoading, isError: error, mutate };
};
